<template>
  <div :class="$style.menu">
    <form ref="formRef" :action="actionPath" @submit.prevent="closeKeyboard">
      <div :class="[$style.section, $style.searchWord]">
        <p :class="$style.sectionTitle">
          キーワード
          <button v-if="q" type="button" @click="() => onClickClearButton('searchWord')">クリア</button>
        </p>
        <input v-model="q" type="text" name="q" enterkeyhint="done" aria-label="キーワード" />
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">カテゴリー</p>
        <ul v-if="initialCategory" :class="$style.categories">
          <li>
            <button
              type="button"
              :class="$style.allCategories"
              @click="
                () => {
                  category = 0;
                  subCategory = 0;
                  nextTick(() => {
                    onSubmit();
                  });
                }
              "
            >
              <i class="icon-arrow-left" />全てのカテゴリー
            </button>
          </li>
          <li>
            <button
              type="button"
              :class="$style.parentCategory"
              :aria-current="!initialSubCategory"
              @click="
                () => {
                  subCategory = 0;
                  nextTick(() => {
                    onSubmit();
                  });
                }
              "
            >
              {{ categories?.find((cat) => cat.slug === initialCategory)?.name || '' }}
            </button>
            <ul :class="$style.subCategories">
              <li v-for="subCat in subCategories" :key="subCat.categoryId" :aria-current="subCat.slug === initialSubCategory">
                <button
                  type="button"
                  @click="
                    () => {
                      subCategory = subCat.categoryId;
                      nextTick(() => {
                        onSubmit();
                      });
                    }
                  "
                >
                  {{ subCat.name }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
        <ul v-else :class="$style.categories">
          <li v-for="cat in categories" :key="cat.categoryId">
            <button
              type="button"
              @click="
                () => {
                  category = cat.categoryId;
                  nextTick(() => {
                    onSubmit();
                  });
                }
              "
            >
              {{ cat.name }}
            </button>
          </li>
        </ul>
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">価格<button v-if="fromPrice || toPrice" type="button" @click="() => onClickClearButton('price')">クリア</button></p>
        <div :class="$style.priceSortWrap">
          <div :class="$style.priceInputWrap">
            <select v-model="fromPrice" name="from_price" aria-label="下限価格">
              <option value=""></option>
              <option v-for="price in prices" :key="price" :value="price">{{ Number(price).toLocaleString() }}</option>
            </select>
          </div>
          <div>〜</div>
          <div :class="$style.priceInputWrap">
            <select v-model="toPrice" name="to_price" aria-label="上限価格">
              <option value=""></option>
              <option v-for="price in prices" :key="price" :value="price">{{ Number(price).toLocaleString() }}</option>
            </select>
          </div>
        </div>
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">素材<button v-if="material" type="button" @click="() => onClickClearButton('material')">クリア</button></p>
        <select v-model="material" name="material" aria-label="素材">
          <option value="">すべての素材</option>
          <option v-for="item in materials()" :key="item.materialId" :value="item.slug">{{ item.name }}</option>
        </select>
      </div>

      <div :class="$style.section">
        <p :class="$style.sectionTitle">色<button v-if="color" type="button" @click="() => onClickClearButton('color')">クリア</button></p>
        <ul class="color-selection" :class="$style.colors">
          <li v-for="item in colorList" :key="item.colorId">
            <input :id="`color_${item.slug}`" v-model="color" name="color" type="radio" :value="item.slug" />
            <label :for="`color_${item.slug}`" :title="item.name" :aria-label="item.name"><div :class="`color-${item.slug}`"></div></label>
          </li>
        </ul>
      </div>

      <div :class="[$style.section, $style.checkbox]">
        <p>
          <label><input v-model="vintage" type="checkbox" name="vintage" />アンティーク・ヴィンテージ</label>
        </p>
        <p>
          <label><input v-model="gift" type="checkbox" name="gift" />ギフト可</label>
        </p>
        <p>
          <label><input v-model="ordermade" type="checkbox" name="ordermade" />オーダーメイド可</label>
        </p>
        <p>
          <label><input v-model="toggleSelling" type="checkbox" />在庫あり</label>
          <input type="hidden" name="selling" :value="selling" />
        </p>
      </div>
      <div :class="$style.submit">
        <IButton type="primary" :native-type="isSp ? 'button' : 'submit'" size="small" @click="onSubmit">絞り込む</IButton>
        <input v-if="sort" type="hidden" name="sort" :value="sort" />
        <input v-if="order" type="hidden" name="order" :value="order" />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { categories as getCategories } from '@/js/data/category';
import { colors } from '@/js/data/color';
import { materials } from '@/js/data/material';
import IButton from '@/js/components/molecules/IButton.vue';

const props = defineProps<{
  initialQuery: string;
  initialCategory: string;
  initialSubCategory: string;
  initialFromPrice: string;
  initialToPrice: string;
  initialMaterial: string;
  initialColor: string;
  initialGift: string;
  initialOrdermade: string;
  initialVintage: string;
  initialSelling: string;
  sort: string;
  order: string;
  isSp?: boolean;
}>();
const prices = ['500', '1000', '3000', '5000', '10000', '15000', '20000', '30000', '50000', '100000'];
const formRef = ref<HTMLFormElement>();
const q = ref(props.initialQuery);
const fromPrice = ref(props.initialFromPrice);
const toPrice = ref(props.initialToPrice);
const category = ref(0);
const categories = getCategories({ parentCategoryId: 0 });
const subCategory = ref(0);
const subCategories = ref<
  {
    categoryId: number;
    slug: string;
    name: string;
  }[]
>();
const material = ref(props.initialMaterial);
const color = ref(props.initialColor);
const colorList = [
  ...colors(),
  {
    colorId: 0,
    slug: 'none',
    name: '選択なし',
  },
];
const gift = ref(props.initialGift === '1');
const ordermade = ref(props.initialOrdermade === '1');
const vintage = ref(props.initialVintage === '1');
const toggleSelling = ref(props.initialSelling === '1');

const categorySlug = computed(() => categories.find((cat) => cat.categoryId === category.value)?.slug || '');
const subCategorySlug = computed(() => subCategories.value?.find((subCat) => subCat.categoryId === subCategory.value)?.slug || '');
const actionPath = computed(() => {
  let path = '/listing';

  if (categorySlug.value) {
    path = `/category/${categorySlug.value}`;

    if (subCategorySlug.value) {
      path += `/${subCategorySlug.value}`;
    }
  }

  return path;
});
const selling = computed(() => (toggleSelling.value ? 'on' : 'off'));

const closeKeyboard = () => {
  const activeElement = document.activeElement as HTMLInputElement;
  if (activeElement) {
    activeElement.blur();
  }
};
const onClickClearButton = (target: string) => {
  switch (target) {
    case 'searchWord': {
      q.value = '';
      break;
    }
    case 'price': {
      fromPrice.value = '';
      toPrice.value = '';
      break;
    }
    case 'material': {
      material.value = '';
      break;
    }
    case 'color': {
      color.value = '';
      break;
    }
  }
};
const onSubmit = () => {
  formRef.value!.submit();
  if (q.value) {
    window.dataLayer?.push({
      event: 'search',
      search_term: q.value,
    });
  }
};

onMounted(() => {
  const initialCategory = categories.find((cat) => cat.slug === props.initialCategory);
  if (initialCategory) {
    category.value = initialCategory.categoryId;
    subCategories.value = getCategories({ parentCategoryId: category.value });
  }
  const initialSubCategory = subCategories.value?.find((cat) => cat.slug === props.initialSubCategory);
  if (initialSubCategory) {
    subCategory.value = initialSubCategory.categoryId;
  }
});
</script>

<style module lang="scss">
.menu {
  form {
    width: 100%;

    @media (--sp) {
      padding: 10px 20px 20px;
    }
  }

  .section {
    margin-bottom: 24px;
  }

  .sectionTitle {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 6px;

    button {
      border-bottom: 1px dashed var(--color-gray600);
      color: var(--color-gray800);
      display: inline-block;
      font-size: 12px;
      margin-left: 10px;
      position: relative;
      top: -1px;

      &:hover {
        opacity: 0.7;
      }
    }
  }

  select {
    height: 35px;
    padding-bottom: 6px;
    padding-top: 6px;
    width: 100%;

    @media (--sp) {
      height: 38px;
    }
  }

  .categories {
    button {
      margin-left: -4px;
      padding: 4px;
      text-align: left;

      &:hover {
        background-color: var(--color-gray100);
      }

      &.allCategories {
        align-items: center;
        display: flex;
        gap: 2px;
        margin-bottom: 4px;
        padding-left: 0;

        i {
          font-size: 18px;
        }
      }

      &.parentCategory[aria-current='true'] {
        font-weight: bold;
      }
    }
  }

  .subCategories {
    margin-left: 6px;

    li[aria-current='true'] button {
      font-weight: bold;
    }
  }

  .searchWord {
    input[type='text'] {
      text-indent: 0;
      width: 100%;
    }
  }

  .priceSortWrap {
    align-items: center;
    display: flex;
    gap: 4px;
    justify-content: space-between;
  }

  .priceInputWrap {
    flex: 1;
    position: relative;

    @media (--pc) {
      font-size: 12px;
    }

    &::before {
      content: '\00a5';
      left: 5px;
      position: absolute;
      top: 9px;

      @media (--pc) {
        top: 8px;
      }
    }

    select {
      text-indent: 8px;
      width: 100%;

      @media (--pc) {
        font-size: 12px;
        text-indent: 6px;
      }
    }
  }

  .colors li:last-child {
    display: none;
  }

  .checkbox {
    padding: 16px 0;

    @media (--pc) {
      margin-bottom: 10px;
    }

    p {
      margin-bottom: 10px;

      label {
        align-items: flex-start;
        cursor: pointer;
        display: flex;
        gap: 4px;

        @media (--sp) {
          align-items: center;

          input {
            height: 16px;
            width: 16px;
          }
        }
      }
    }
  }

  .submit {
    margin-top: 10px;

    @media (--sp) {
      background-color: var(--color-white);
      bottom: 0;
      box-shadow: 0 -1px 3px 0 var(--color-black-alpha10);
      margin: -20px;
      padding: 12px 10px;
      position: sticky;
      z-index: 1;
    }
  }
}
</style>
